import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Image } from "@/components/ui/image";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MedalDisplayCase } from "@/features/user/components/medal-display-case";
import { queries } from "@/utils/query";
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useChildMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useUpdateEffect } from "ahooks";
import {
  CircleHelpIcon,
  FilePenLineIcon,
  MailIcon,
  MarsStrokeIcon,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

export const Route = createFileRoute("/user/$username")({
  beforeLoad: ({ context, location, params }) => {
    // Redirect to the user's profile if they are already authenticated
    if (["/user", "/user/"].includes(location.pathname)) {
      if (context.auth.session) {
        throw redirect({
          to: "/user/$username/gallery",
          params: {
            username: params.username,
          },
        });
      }

      // Otherwise, redirect to the login page
      throw redirect({
        to: "/auth/login",
      });
    }

    if (context.auth.session) {
      return {
        // isCurrentUser: currentUser.username === params.username,
        isCurrentUser: context.auth.user.userId === params.username,
      };
    }

    return {
      isCurrentUser: false,
    };
  },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      queries.user.getUser.getFetchOptions({ id: params.username }),
    ),
  context: () => ({
    features: ["fixed-header"],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { isCurrentUser } = Route.useRouteContext();

  const { username } = Route.useParams();
  const { data: user } = queries.user.getUser.useSuspenseQuery({
    variables: { id: username },
  });

  const navigate = useNavigate({ from: "/user/$username" });
  const childMatches = useChildMatches();
  const [currentTab, setCurrentTab] = useState(
    () =>
      childMatches.at(0)?.routeId.replace("/user/$username/", "") ?? "gallery",
  );
  useUpdateEffect(() => {
    navigate({ to: `/user/$username/${currentTab}`, params: { username } });
  }, [currentTab]);

  const followUserMutation = queries.user.followUser.useMutation();

  return (
    <div className="flex flex-col sm:flex-row">
      <div className="flex-1 px-6">
        <div className="relative w-full aspect-toutu">
          <img
            src="https://picsum.photos/800/450"
            alt=""
            className="object-cover size-full"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-subtle-bg to-gray-subtle-bg/20" />
        </div>

        <Tabs
          value={currentTab}
          onValueChange={setCurrentTab}
          className="sticky z-10 py-4 top-18 bg-gray-app-bg"
        >
          <TabsList>
            <TabsTrigger value="gallery">作品集&ensp;8</TabsTrigger>
            <TabsTrigger value="liked">喜欢&ensp;0</TabsTrigger>
            <TabsTrigger value="collections">收藏&ensp;74</TabsTrigger>
            <TabsTrigger value="blogs">博客&ensp;0</TabsTrigger>
            <TabsTrigger value="palettes">色板&ensp;0</TabsTrigger>
            <TabsTrigger value="shops">小铺子&ensp;0</TabsTrigger>
          </TabsList>
        </Tabs>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentTab}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>

        <div className="h-96" />
      </div>

      <div className="sm:max-w-[432px] sm:w-1/4 order-first sm:order-last sm:h-[calc(100vh-4.5rem)]">
        <Card className="h-full border divide-y bg-gray-app-bg sm:fixed sm:top-18 sm:right-0 sm:bottom-0 sm:max-w-[432px] sm:w-1/4">
          <CardHeader className="relative gap-6 isolate">
            <div className="absolute inset-x-0 top-0 aspect-toutu -z-10">
              <img
                src="https://picsum.photos/800/450"
                alt=""
                className="object-cover size-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-subtle-bg to-gray-subtle-bg/20" />
            </div>

            <div className="flex items-end gap-2">
              <Image
                alt=""
                src={user.avatarUrl}
                className="size-24 bg-gray-solid"
                width={96}
                aspectRatio={1}
              />
              <div className="w-full space-y-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-medium">{user.nickname}</h2>
                  <Badge variant="default" size="xs">
                    Lv. 3
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-gray-fg-secondary">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">@{user.username}</span>
                    <MarsStrokeIcon className="text-blue-500 size-4" />
                  </div>
                  <span className="text-xs">IP属地：地球</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-fg-secondary">
              这个人很懒，除了帅气什么都没留下
            </p>

            <div className="flex justify-between">
              <div className="flex gap-6">
                <Link
                  className="flex flex-col items-center"
                  to="/user/$username/following"
                  params={{ username }}
                >
                  <span className="text-base text-gray-fg-primary">15</span>
                  <span className="text-xs text-gray-fg-secondary">关注</span>
                </Link>
                <div className="flex flex-col items-center">
                  <span className="text-base text-gray-fg-primary">122</span>
                  <span className="text-xs text-gray-fg-secondary">粉丝数</span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-base text-gray-fg-primary">8</span>
                  <span className="text-xs text-gray-fg-secondary">作品</span>
                </div>
              </div>

              {isCurrentUser ? (
                <Button intent="soft">
                  <FilePenLineIcon />
                  编辑资料
                </Button>
              ) : (
                <Button intent="soft" flavor="gray" size="icon">
                  <MailIcon />
                </Button>
              )}
            </div>

            {!isCurrentUser && (
              <Button
                intent="soft"
                onClick={() =>
                  followUserMutation.mutate({ followeeId: user.userId })
                }
              >
                关注
              </Button>
            )}
          </CardHeader>

          <CardContent className="pt-3 pb-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">勋章墙</h3>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      intent="plain"
                      flavor="gray"
                      size="icon"
                      className="text-gray-fg-secondary size-5"
                    >
                      <CircleHelpIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>勋章墙是展示用户荣誉的地方</p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <div>
                {/* <p className="text-sm text-gray-fg-secondary">
                  一个勋章都没有噢~
                </p> */}

                <MedalDisplayCase
                  medals={Array.from({ length: 8 }).map((_, index) => ({
                    id: index.toString(),
                    name: "勋章",
                    description: "这是一个勋章",
                    iconUrl: `https://api.dicebear.com/9.x/bottts/svg?seed=Medal-${index}`,
                    order: index,
                  }))}
                />
              </div>
            </div>
          </CardContent>

          <CardContent className="pt-3 pb-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">圈子</h3>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      intent="plain"
                      flavor="gray"
                      size="icon"
                      className="text-gray-fg-secondary size-5"
                    >
                      <CircleHelpIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>圈子是展示用户荣誉的地方</p>
                  </TooltipContent>
                </Tooltip>
              </div>

              <div>
                <p className="text-sm text-gray-fg-secondary">
                  一个圈子都没有噢~
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
